import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Globe, Monitor, Smartphone, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { contactPath } from '@/lib/constants'
import type { Platform, Product, ProductStatus } from '@/lib/products'

/* --------------------------------------------------------------------------
   Small, reusable product building blocks shared by cards, menus and pages.
   -------------------------------------------------------------------------- */

const STATUS_STYLES: Record<ProductStatus, { dot: string; chip: string }> = {
  live: {
    dot: 'bg-emerald-500 animate-pulse-dot',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30',
  },
  beta: {
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/30',
  },
  comingSoon: {
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
  },
}

export function StatusBadge({ status, className }: { status: ProductStatus; className?: string }) {
  const { t } = useTranslation('common')
  const s = STATUS_STYLES[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        s.chip,
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} />
      {t(`status.${status}`)}
    </span>
  )
}

const PLATFORM_ICONS: Record<Platform, typeof Globe> = {
  web: Globe,
  windows: Monitor,
  mobile: Smartphone,
}

export function PlatformChips({ platforms, className }: { platforms: Platform[]; className?: string }) {
  const { t } = useTranslation('common')
  return (
    <ul className={cn('flex flex-wrap gap-1.5', className)}>
      {platforms.map((p) => {
        const Icon = PLATFORM_ICONS[p]
        return (
          <li
            key={p}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
          >
            <Icon className="h-3 w-3" />
            {t(`platforms.${p}`)}
          </li>
        )
      })}
    </ul>
  )
}

export function ProductIconTile({
  product,
  size = 'md',
  className,
}: {
  product: Product
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const Icon = product.icon
  const sizes = {
    sm: 'h-9 w-9 rounded-lg [&>svg]:h-[18px] [&>svg]:w-[18px]',
    md: 'h-11 w-11 rounded-xl [&>svg]:h-5 [&>svg]:w-5',
    lg: 'h-14 w-14 rounded-2xl [&>svg]:h-7 [&>svg]:w-7',
  }
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center bg-gradient-to-br text-white shadow-sm',
        product.accent.gradient,
        sizes[size],
        className
      )}
    >
      <Icon />
    </span>
  )
}

/**
 * The single "primary action" for a product, so every surface behaves the
 * same: live/beta products open the app; private-beta products go to the
 * waitlist form instead of a login wall the visitor can't pass.
 */
export function ProductPrimaryAction({
  product,
  className,
  size = 'md',
  variant = 'solid',
}: {
  product: Product
  className?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'outline' | 'ghost'
}) {
  const { t } = useTranslation('common')
  const sizes = {
    sm: 'h-9 px-3 text-sm',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-base',
  }
  const variants = {
    solid: cn('text-white shadow-sm', product.accent.solid, product.accent.solidHover),
    outline: 'border bg-card hover:bg-accent',
    ghost: cn('hover:bg-accent', product.accent.text),
  }
  const base = cn(
    'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    sizes[size],
    variants[variant],
    className
  )

  if (product.status === 'comingSoon') {
    return (
      <Link to={contactPath({ product: product.id, topic: 'waitlist' })} className={base}>
        <Clock className="h-4 w-4" />
        {t('actions.joinWaitlist')}
      </Link>
    )
  }

  return (
    <a href={product.appUrl} target="_blank" rel="noopener noreferrer" className={base}>
      {t('actions.openApp')}
      <ArrowUpRight className="h-4 w-4 flip-rtl" />
    </a>
  )
}
